import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { convMeta } from "@/pages/ChatApp";
import Avatar from "@/components/chat/Avatar";
import NewChatDialog from "@/components/chat/NewChatDialog";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { timeLabel } from "@/lib/format";
import { Search, UserPlus, MoreVertical, Shield, LogOut, Building2, Check, CheckCheck, KeyRound, ALargeSmall } from "lucide-react";

export default function Sidebar({ me, conversations, users, online, activeId, isMobile, onOpen, onNewConversation, onGoAdmin, reloadUsers, chatScale, onSetScale }) {
  const { logout, refreshMe } = useAuth();
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [noteResults, setNoteResults] = useState([]);
  const searchTimer = useRef(null);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    const term = query.trim();
    if (term.length < 2) { setNoteResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q: term } }); setNoteResults(data); }
      catch { setNoteResults([]); }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [query]);

  const savePin = async () => {
    if (!/^\d{4}$/.test(pinValue)) { toast.error("Il PIN deve avere esattamente 4 cifre"); return; }
    setPinBusy(true);
    try {
      await api.post("/auth/pin", { pin: pinValue });
      await refreshMe();
      toast.success("PIN impostato");
      setPinValue(""); setPinOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPinBusy(false); }
  };
  const removePin = async () => {
    setPinBusy(true);
    try {
      await api.delete("/auth/pin");
      await refreshMe();
      toast.success("PIN rimosso");
      setPinOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPinBusy(false); }
  };

  const items = useMemo(() => {
    return conversations
      .map((c) => ({ conv: c, meta: convMeta(c, me.id, online) }))
      .filter(({ conv, meta }) => {
        if (!query.trim()) return true;
        const q = query.toLowerCase();
        return (
          meta.title.toLowerCase().includes(q) ||
          (conv.lastMessage?.text || "").toLowerCase().includes(q)
        );
      });
  }, [conversations, me.id, online, query]);

  return (
    <aside className="w-full md:w-80 lg:w-96 flex-shrink-0 flex flex-col h-full border-r border-border bg-[hsl(var(--sidebar))]">
      <header className="px-4 py-3 flex items-center justify-between border-b border-border/80 bg-background/90 backdrop-blur-md sticky top-0 z-20">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-sky-500 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold text-lg tracking-tight">Relatio</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" data-testid="new-chat-button" onClick={() => setDialogOpen(true)} title="Nuovo contatto">
            <UserPlus className="h-5 w-5" />
          </Button>
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu-button">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-2">
                <div className="text-sm font-semibold truncate">{me.displayName}</div>
                <div className="text-xs text-muted-foreground truncate">{me.email}</div>
              </div>
              <DropdownMenuSeparator />
              {me.role === "admin" && (
                <DropdownMenuItem data-testid="admin-panel-button" onClick={onGoAdmin}>
                  <Shield className="h-4 w-4 mr-2" /> Pannello Amministrazione
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="pin-settings-button" onClick={() => setPinOpen(true)}>
                <KeyRound className="h-4 w-4 mr-2" /> PIN di sblocco {me.pinSet ? "(attivo)" : ""}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
                <ALargeSmall className="h-4 w-4" /> Dimensione testo
              </DropdownMenuLabel>
              {[{ n: 1, label: "Normale" }, { n: 2, label: "Grande" }, { n: 3, label: "Molto grande" }].map((o) => (
                <DropdownMenuItem key={o.n} data-testid={`text-size-${o.n}`}
                  onSelect={(e) => { e.preventDefault(); onSetScale?.(o.n); }}>
                  <span className="flex-1">{o.label}</span>
                  {(chatScale || 1) === o.n && <Check className="h-4 w-4 text-sky-500" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={pinOpen} onOpenChange={setPinOpen}>
            <DialogContent data-testid="pin-dialog">
              <DialogHeader>
                <DialogTitle>PIN di sblocco veloce</DialogTitle>
                <DialogDescription>
                  Imposta un PIN di 4 cifre per sbloccare rapidamente l'app dopo il blocco automatico, senza reinserire la password.
                </DialogDescription>
              </DialogHeader>
              <Input inputMode="numeric" pattern="\d*" maxLength={4} placeholder="es. 1357"
                value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
                data-testid="pin-set-input" className="text-center tracking-[0.3em] text-lg" />
              <div className="flex gap-2 justify-end">
                {me.pinSet && (
                  <Button variant="outline" onClick={removePin} disabled={pinBusy} data-testid="pin-remove-button">Rimuovi PIN</Button>
                )}
                <Button className="bg-sky-600 hover:bg-sky-700 text-white" onClick={savePin} disabled={pinBusy} data-testid="pin-save-button">
                  {me.pinSet ? "Aggiorna PIN" : "Imposta PIN"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="conversation-search-input"
            className="pl-10 h-10 bg-muted/60 border-transparent focus-visible:bg-background"
            placeholder="Cerca fornitori o clienti..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto thin-scroll px-2 pb-4" data-testid="conversation-list">
        {items.length === 0 && (
          <div className="text-center text-sm text-muted-foreground mt-16 px-6">
            Nessuna scheda. Tocca l'icona in alto per aggiungere un contatto.
          </div>
        )}
        {items.map(({ conv, meta }) => {
          const active = conv.id === activeId;
          const last = conv.lastMessage;
          const fromMe = last?.senderId === me.id;
          return (
            <button
              key={conv.id}
              data-testid={`conversation-item-${conv.id}`}
              onClick={() => onOpen(conv.id)}
              className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-colors mb-0.5 ${
                active ? "bg-sky-500/10 ring-1 ring-sky-500/30" : "hover:bg-muted/70"
              }`}
            >
              <Avatar name={meta.title} online={meta.onlineNow} isGroup={meta.isGroup} size={46} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm truncate">{meta.title}</span>
                  {last && <span className="text-[11px] text-muted-foreground font-mono flex-shrink-0">{timeLabel(last.createdAt)}</span>}
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground truncate flex items-center gap-1">
                    {fromMe && <CheckCheck className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />}
                    {last ? last.text : "Nessuna nota"}
                  </span>
                  {conv.unreadCount > 0 && (
                    <span data-testid={`unread-badge-${conv.id}`} className="bg-sky-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center flex-shrink-0">
                      {conv.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}

        {query.trim().length >= 2 && noteResults.length > 0 && (
          <div className="mt-3" data-testid="global-search-results">
            <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Nelle note ({noteResults.length})</div>
            {noteResults.map((r) => {
              const conv = conversations.find((c) => c.id === r.conversationId);
              const meta = conv ? convMeta(conv, me.id, online) : { title: "Scheda", isGroup: false };
              return (
                <button key={r.messageId} data-testid={`search-result-${r.messageId}`}
                  onClick={() => onOpen(r.conversationId, query.trim())}
                  className="w-full text-left flex items-start gap-3 p-2.5 rounded-xl hover:bg-muted/70 transition-colors mb-0.5">
                  <Avatar name={meta.title} isGroup={meta.isGroup} showStatus={false} size={38} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{meta.title}</span>
                      <span className="text-[11px] text-muted-foreground font-mono flex-shrink-0">{timeLabel(r.createdAt)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{r.text}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <NewChatDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        me={me}
        users={users}
        onCreated={(conv) => { setDialogOpen(false); onNewConversation(conv); }}
        reloadUsers={reloadUsers}
      />
    </aside>
  );
}
